import React from "react";
import HeroSection from "../components/HeroSection";
import { heroSections } from "../constants/data.ts";
import images from "../constants/images.ts";

const staffingSolutions = [
  {
    title: "IT Staffing",
    description:
      "Find highly skilled IT professionals with the technical expertise needed to drive your projects and business objectives.",
    listTitle: "Our IT Staffing Services Include:",
    image: images.staffingServices.itStaffing,
    items: [
      "Software Developers",
      "Full Stack Developers",
      "Frontend & Backend Developers",
      "Mobile App Developers",
      "UI/UX Designers",
      "QA & Testing Engineers",
      "DevOps Engineers",
      "Cloud Engineers",
      "Data Engineers",
      "AI & Machine Learning Professionals",
      "Cybersecurity Experts",
      "IT Project Managers",
    ],
    benefits: [
      "Access to Skilled IT Talent",
      "Faster Hiring Process",
      "Reduced Recruitment Costs",
      "Flexible Hiring Models",
      "Improved Project Delivery",
    ],
  },
  {
    title: "Permanent Staffing",
    description:
      "Hire qualified professionals for long-term roles who align with your company culture and business goals.",
    listTitle: "Our Permanent Staffing Services Include:",
    image: images.staffingServices.permanentStaffing,
    items: [
      "Talent Sourcing",
      "Candidate Screening",
      "Technical Assessments",
      "Interview Coordination",
      "Background Verification",
      "Salary Benchmarking",
      "Offer Management",
      "Onboarding Support",
    ],
    benefits: [
      "Long-Term Employee Retention",
      "Improved Workforce Stability",
      "Reduced Hiring Risks",
      "Better Organizational Growth",
      "High-Quality Talent Acquisition",
    ],
  },
  {
    title: "Contract Staffing",
    description:
      "Meet short-term business demands with experienced professionals available for temporary or project-based assignments.",
    listTitle: "Our Contract Staffing Services Include:",
    image: images.staffingServices.contractStaffing,
    items: [
      "Project-Based Hiring",
      "Temporary Staffing",
      "Contract-to-Hire",
      "Seasonal Workforce Support",
      "Technical Consultants",
      "Specialized Resource Deployment",
      "Payroll Coordination",
      "Compliance Support",
    ],
    benefits: [
      "Workforce Flexibility",
      "Faster Resource Deployment",
      "Reduced Employment Costs",
      "Easy Workforce Scaling",
      "Support for Critical Projects",
    ],
  },
  {
    title: "Remote Resource Hiring",
    description:
      "Access top talent from anywhere and build productive remote teams without geographical limitations.",
    listTitle: "Our Remote Hiring Services Include:",
    image: images.staffingServices.remoteResourceHiring,
    items: [
      "Remote Software Developers",
      "Dedicated Development Teams",
      "Virtual Project Teams",
      "Remote Technical Support",
      "Digital Marketing Professionals",
      "Designers & Creative Experts",
      "Business Support Staff",
      "Global Talent Sourcing",
    ],
    benefits: [
      "Access to Global Talent",
      "Reduced Infrastructure Costs",
      "Increased Business Flexibility",
      "Faster Hiring Process",
      "Enhanced Productivity",
    ],
  },
  {
    title: "Workforce Management",
    description:
      "Optimize your workforce with strategic planning, resource allocation, and employee management solutions.",
    listTitle: "Our Workforce Management Services Include:",
    image: images.staffingServices.workforceManagement,
    items: [
      "Workforce Planning",
      "Resource Allocation",
      "Employee Performance Tracking",
      "Attendance & Shift Planning",
      "Payroll Coordination",
      "Compliance Management",
      "HR Support Services",
      "Workforce Analytics & Reporting",
    ],
    benefits: [
      "Improved Operational Efficiency",
      "Better Workforce Utilization",
      "Enhanced Employee Productivity",
      "Regulatory Compliance",
      "Data-Driven Workforce Decisions",
    ],
  },
];

const processSteps = [
  "Requirement Analysis",
  "Talent Sourcing",
  "Candidate Screening",
  "Technical & HR Evaluation",
  "Interview Coordination",
  "Candidate Selection",
  "Offer Management",
  "Onboarding Support",
  "Post-Placement Follow-Up",
];

const reasons = [
  "Experienced Recruitment Specialists",
  "Extensive Talent Network",
  "Industry-Specific Hiring Expertise",
  "Quick Turnaround Time",
  "Customized Staffing Solutions",
  "Rigorous Candidate Screening",
  "Transparent Recruitment Process",
  "Cost-Effective Hiring",
  "Flexible Engagement Models",
  "Dedicated Client Support",
];

const industries = [
  "Information Technology",
  "Healthcare",
  "Banking & Financial Services",
  "Manufacturing",
  "Retail & E-commerce",
  "Telecommunications",
  "Education",
  "Logistics & Supply Chain",
  "Engineering",
  "Hospitality",
  "Real Estate",
  "Startups & Enterprises",
];

const StaffingService = () => {
  return (
    <main className="bg-background">
      <HeroSection {...heroSections.staffingService} />

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <p className="mb-3 heading-main text-primary">Staffing Services</p>
          <h2 className="heading-main mb-5">
            Build the Right Team with Reliable Staffing Solutions
          </h2>
          <div className="space-y-4">
            <p className="text-body">
              Hiring the right talent is essential for business success. At
              Westlisoft, we provide end-to-end staffing solutions that help
              organizations find skilled professionals quickly and efficiently.
              Whether you need permanent employees, contract resources, or
              remote teams, we connect you with qualified candidates who match
              your business requirements.
            </p>
            <p className="text-body">
              Our recruitment experts leverage industry knowledge, a strong
              talent network, and a streamlined hiring process to reduce
              recruitment time, improve hiring quality, and support your
              workforce growth.
            </p>
          </div>
        </div>
      </section>

      <section className="px-outer py-16 md:pb-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 heading-main text-primary">
              Our Staffing Solutions
            </p>
          </div>

          <div className="space-y-10 md:space-y-14">
            {staffingSolutions.map((solution, index) => {
              const imageFirst = index % 2 === 1;

              return (
                <article
                  key={solution.title}
                  className="grid items-center gap-6 rounded-card bg-white p-5 shadow-sm md:grid-cols-2 md:gap-10 lg:p-8"
                >
                  <div
                    className={
                      imageFirst
                        ? "order-2 md:order-2"
                        : "order-2 md:order-1"
                    }
                  >
                    <h3 className="mb-4 font-rubik text-2xl font-bold text-secondary md:text-3xl">
                      {solution.title}
                    </h3>
                    <p className="text-body mb-5">{solution.description}</p>
                    <h4 className="mb-3 font-rubik text-lg font-semibold text-secondary">
                      {solution.listTitle}
                    </h4>
                    <ul className="mb-5 grid gap-2 text-sm font-light text-gray-700 sm:grid-cols-2">
                      {solution.items.map((item) => (
                        <li key={item} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <h4 className="mb-3 font-rubik text-lg font-semibold text-secondary">
                      Benefits
                    </h4>
                    <ul className="grid gap-2 text-sm font-light text-gray-700 sm:grid-cols-2">
                      {solution.benefits.map((benefit) => (
                        <li key={benefit} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className={
                      imageFirst
                        ? "order-1 flex h-60 items-center justify-center rounded-card bg-gray-50 p-3 sm:h-72 md:order-1 md:h-[320px] lg:h-[360px]"
                        : "order-1 flex h-60 items-center justify-center rounded-card bg-gray-50 p-3 sm:h-72 md:order-2 md:h-[320px] lg:h-[360px]"
                    }
                  >
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="h-full w-full object-contain"
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <p className="mb-4 heading-main text-primary">
            Our Staffing Process
          </p>
          <p className="text-body mx-auto mb-8 max-w-3xl">
            We follow a structured recruitment process to ensure you hire the
            right talent efficiently.
          </p>
          <ol className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {processSteps.map((step, index) => (
              <li
                key={step}
                className="flex min-h-24 flex-col items-center justify-center rounded-card bg-background px-4 py-5 text-center font-rubik text-sm font-light text-gray-700"
              >
                <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary font-bold text-secondary">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <h2 className="heading-main mb-8 text-primary">
            Why Choose Westlisoft?
          </h2>
          <ul className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {reasons.map((reason) => (
              <li
                key={reason}
                className="flex min-h-20 items-center justify-center rounded-card bg-white px-4 py-4 text-center font-rubik text-sm font-light text-gray-700 shadow-sm"
              >
                {reason}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <p className="mb-4 heading-main text-primary">
            Industries We Serve
          </p>
          <p className="text-body mx-auto mb-8 max-w-3xl">
            We provide staffing solutions across a wide range of industries,
            including:
          </p>
          <ul className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((industry) => (
              <li
                key={industry}
                className="flex min-h-16 items-center justify-center rounded-card bg-background px-4 py-4 text-center font-rubik text-sm font-light text-gray-700"
              >
                {industry}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-5xl text-center">
          <h2 className="heading-main mb-4 text-primary">
            Find the Right Talent, Faster
          </h2>
          <div className="mx-auto max-w-4xl space-y-4">
            <p className="text-body">
              At Westlisoft, we are committed to helping businesses build
              high-performing teams through reliable, scalable, and
              cost-effective staffing solutions. Whether you need a single
              specialist or an entire workforce, our recruitment experts ensure
              you have access to the right talent at the right time.
            </p>
            <p className="text-body">
              Partner with Westlisoft to simplify your hiring process and build
              a stronger workforce for sustainable business growth.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default StaffingService;
